import { useCallback, useEffect, useState } from 'react';

import { useDeleteDocument } from '@/features/Documents/api';
import {
    fetchAllPaginatedItems,
    listDocuments,
} from '@/features/end-of-lease/api';
import { DocumentCard } from '@/features/end-of-lease/document-card';
import { DocumentsTable } from '@/features/end-of-lease/documents-table';
import type { LeasingDocs } from '@/models/docs';
import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ChevronDown, Filter, Search } from 'lucide-react';

import { parseOptionalPage } from '@/lib/urlUtils';

import { useAuthAxios } from '@/hooks/useAuthAxios';
import { usePagination } from '@/hooks/usePagination';
import { useSearchParamFilters } from '@/hooks/useSearchParamFilters';
import { useSearchParamState } from '@/hooks/useSearchParamState';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

interface ProjectDocumentsSearch {
    page?: number;
    filter?: string;
}

export const Route = createFileRoute('/lease/projects/$projectId/documents/')({
    component: ProjectDocumentsDetailPage,
    validateSearch: (search: Record<string, unknown>): ProjectDocumentsSearch => ({
        page: parseOptionalPage(search.page),
        filter: typeof search.filter === 'string' ? search.filter : undefined,
    }),
});

const LIMIT = 12;

// `type: ''` means every type; encodeFilters/decodeFilters can't round-trip null.
const DOCUMENTS_FILTER_DEFAULTS = { search: '', type: '' };

export function ProjectDocumentsDetailPage() {
    const { projectId: _projectId } = Route.useParams();
    const projectId = Number(_projectId);

    const [totalElements, setTotalElements] = useState(0);
    const [selectedDocument, setSelectedDocument] =
        useState<LeasingDocs | null>(null);
    const { value: page, setValue: setPage } = useSearchParamState(
        'page',
        parseOptionalPage,
    );
    const { limit, skip, Pagination } = usePagination({
        totalElements,
        limit: LIMIT,
        page,
        onPageChange: setPage,
    });

    const { client, enabled } = useAuthAxios();
    const [allDocuments, setAllDocuments] = useState<LeasingDocs[]>([]);

    const deleteDocument = useDeleteDocument();

    const [loading, setLoading] = useState(false);

    // TODO: Refactor the queries below to have useAuthAxios inside them by default
    const fetchAllDocuments = useCallback(
        async (selectedDocumentId?: number) => {
            setLoading(true);
            const allFetchedItems = await fetchAllPaginatedItems<LeasingDocs>(
                ({ limit, skip }) =>
                    listDocuments({
                        client,
                        projectId,
                        limit,
                        skip,
                    }),
            );
            setAllDocuments(allFetchedItems);
            setLoading(false);

            // Update selectedDocument to the latest version
            if (selectedDocumentId) {
                const updatedDoc = allFetchedItems.find(
                    (doc) => doc.id === selectedDocumentId,
                );
                if (updatedDoc) setSelectedDocument(updatedDoc);
            }
        },
        [projectId],
    );

    useEffect(() => {
        fetchAllDocuments();
    }, [fetchAllDocuments]);

    const { filters, setFilters } = useSearchParamFilters(
        'filter',
        DOCUMENTS_FILTER_DEFAULTS,
    );
    const searchTerm = filters.search;
    const typeFilter = filters.type || null;

    const filteredDocuments = allDocuments.filter((doc) => {
        const matchesSearch =
            searchTerm === '' ||
            doc?.meta?.raw?.id
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            doc?.meta?.raw?.title
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase());

        const matchesType =
            typeFilter === null || doc?.meta?.raw?.type === typeFilter;

        return matchesSearch && matchesType;
    });

    const documentTypes = Array.from(
        new Set(
            allDocuments
                .map((doc) => doc?.meta?.raw?.type)
                .filter((type) => type),
        ),
    );

    useEffect(() => {
        if (filteredDocuments) setTotalElements(filteredDocuments.length);
    }, [filteredDocuments]);

    const pageDocs = filteredDocuments.slice(skip, skip + limit);

    const handleSearchChange = (newSearchTerm: string) => {
        setFilters({ search: newSearchTerm }, { replace: true });
    };

    const handleTypeFilterChange = (newTypeFilter: string | null) => {
        setFilters({ type: newTypeFilter ?? '' });
    };

    return (
        <div
            className="flex-1 bg-white min-h-full"
            data-testid="LeaseProjectDocuments"
        >
            <main className="flex flex-1 overflow-hidden flex-col">
                <div className="flex flex-row w-full justify-end p-8">
                    <Link
                        to="/documents/upload"
                        search={{
                            projectId: Number(projectId),
                            isProjectView: true,
                        }}
                    >
                        <Button>Upload new file</Button>
                    </Link>
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex flex-row">
                        <div className="flex flex-col flex-1 overflow-hidden">
                            <div className="flex-1 p-8 overflow-auto space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 w-1/3">
                                        <div className="relative w-full">
                                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                                            <Input
                                                placeholder="Search documents..."
                                                className="pl-8"
                                                value={searchTerm}
                                                onChange={(e) =>
                                                    handleSearchChange(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="flex items-center gap-1"
                                            >
                                                <Filter className="h-4 w-4" />
                                                Filter by Type
                                                <ChevronDown className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                                onClick={() =>
                                                    handleTypeFilterChange(
                                                        null,
                                                    )
                                                }
                                            >
                                                All Types
                                            </DropdownMenuItem>
                                            {documentTypes.map((type) => (
                                                <DropdownMenuItem
                                                    key={type}
                                                    onClick={() =>
                                                        handleTypeFilterChange(
                                                            type ||
                                                                'ServiceBulletin',
                                                        )
                                                    }
                                                >
                                                    {type}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                                <DocumentsTable
                                    documents={pageDocs}
                                    onSelectDocument={setSelectedDocument}
                                    onDeleteDocument={async (doc) =>
                                        await deleteDocument.mutateAsync(doc.id)
                                    }
                                    selectedDocumentId={selectedDocument?.id}
                                    loading={loading}
                                />
                            </div>
                        </div>

                        {selectedDocument && selectedDocument?.meta?.raw && (
                            <div className="w-1/4 border-l">
                                <DocumentCard
                                    id={selectedDocument.id}
                                    leasing_document={selectedDocument}
                                    refetch={() =>
                                        fetchAllDocuments(selectedDocument?.id)
                                    }
                                    onClose={() => setSelectedDocument(null)}
                                />
                            </div>
                        )}
                    </div>

                    <Pagination />
                </div>
            </main>
        </div>
    );
}
